import { IFreelancerSearchResponse } from '../../interfaces/freelancerSearch';
import { ITableAction, ITableColumn, ITableData } from '../../interfaces/tableProps';
import { useMemo } from 'react';
import AppTable from '../common/AppTable';
import { theme } from '../../theme';
import { ColumnFlexBox } from '../common/boxes';
import SearchHeader from '../common/SearchHeader';
import { Box } from '@mui/material';
import { ISearchHeaderInputProps, ISearchHeaderProps } from '../../interfaces/searchHeaderProps';
import { CUSTOMER_STATUS_MAP, CustomerStatus } from '../../enums/customerStatus';
import { IClientAdminSearch } from '../../interfaces/clientAdminSearch';
import { CheckCircle, AccountTree } from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientService } from '../../services/client';
import { useNotification } from '../../+state/useNotification';
import { TEXTS } from '../../core/texts';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../interfaces/errorResponse';
import { parseAxiosError } from '../../services/axios';
import { CLIENT_LIST_QUERY_KEY } from './Clients';

const columns: ITableColumn<keyof IClientAdminSearch>[] = [
  {
    name: 'id',
    label: 'ID',
    sx: { '&:hover': { cursor: 'pointer', textDecoration: 'underline', color: theme.palette.primary.dark } },
    withCopyIcon: true
  },
  { name: 'legalName', label: 'Legal Name' },
  { name: 'email', label: 'Email' },
  { name: 'contactName', label: 'Contact Name' },
  { name: 'phone', label: 'Phone' },
  { name: 'status', label: 'Status', format: (val: CustomerStatus) => CUSTOMER_STATUS_MAP[val] },
  { name: 'registered', label: 'Registered', format: (val: boolean) => val.toString() },
  { name: 'registrationDate', label: 'Registration Date', format: (val) => val.split('T')[0] },
];

interface IProps {
  tableData: ITableData<IClientAdminSearch>;
  searchProps: Omit<ISearchHeaderProps, 'inputProps'>;
  searchInputProps: ISearchHeaderInputProps[]
}

const ClientsTableWithSearch = ({ tableData, searchProps, searchInputProps }: IProps) => {
  const { openErrorToast, openSuccessToast } = useNotification();
  const queryClient = useQueryClient();
  const handlers: { [key: string]: (val: IFreelancerSearchResponse) => void } = useMemo(() => ({
    id: (f) => window.open(`/clients/${f.id}`, '_blank', 'noopener,noreferrer'),
  }), []);

  const completeMutation = useMutation({
    mutationFn: (id: number) => ClientService.completeCustomer(id),
    onSuccess: () => {
      openSuccessToast(TEXTS.REGISTRATION_COMPLETED);
      queryClient.invalidateQueries([CLIENT_LIST_QUERY_KEY]);
    },
    onError: (err: AxiosError<IErrorResponse>) => openErrorToast(parseAxiosError(err)),
  });

  const actions = useMemo<ITableAction[]>(() => ([
    {
      icon: <CheckCircle color={'primary'}/>,
      tooltip: 'Complete registration',
      condition: (client: IClientAdminSearch) => client.status !== CustomerStatus.ACTIVE,
      onClick: (client: IClientAdminSearch) => completeMutation.mutate(client.id)
    },
    {
      icon: <AccountTree color={'primary'}/>,
      tooltip: 'View opportunities',
      condition: (client: IClientAdminSearch) => client.status == CustomerStatus.ACTIVE,
      onClick: (client: IClientAdminSearch) => window.open(`/opportunities/projects?clientId=${client.id}`, '_blank', 'noopener,noreferrer')
    }
  ]), [completeMutation]);

  return (
    <ColumnFlexBox>
      <Box mb={6}>
        <SearchHeader {...searchProps} initialExpanded={true} inputProps={searchInputProps}/>
      </Box>
      <AppTable data={tableData} columns={columns} handlers={handlers} labelRowsPerPage={'Clients per page'}
                actions={actions}/>
    </ColumnFlexBox>
  );
};

export default ClientsTableWithSearch;